import { Component, State, h } from '@stencil/core';
import state from '@ugc-store';
import { postAnswer } from '@ugc-api/question';
import {
  addDataLayer,
  handleErrorMessage,
  login,
  MAX_ANSWER_CHARACTERS,
  MIN_CHARACTERS,
  showToast,
  stringFormat,
  hasHtmlTag,
  xssFilter,
} from '@ugc-utils/utils';
import { filterQuestionInfo } from '@ugc-components/ugc-pdp-question-and-answer/utils';
import { modalZIndex } from '@ugc-utils/modal-z-index';

@Component({
  tag: 'ugc-qna-answer-modal',
  styleUrl: 'ugc-qna-answer-modal.scss',
  shadow: false,
})
export class UgcQnaAnswerModal {
  @State() answerText: string = '';
  @State() isSubmitDisabled: boolean = true;
  @State() isTextareaError: boolean = false;
  @State() textareaMessage: string = '';

  async handleSubmit() {
    const {
      qnaStaticLanguage: { IDS_UGC_QNA_TOAST_THANKS, IDS_UGC_QNA_TOAST_CHECK },
      answerModalData: { questionId },
      productInfo: { productId },
      isLogin,
    } = state;

    if (!isLogin) {
      login();
      return;
    }

    if (this.isSubmitDisabled) return;

    // textarea error
    if (!this.validateContent(this.answerText)) return;

    this.resetTextareaErrorMessage();

    const data = { productId, qid: questionId, content: xssFilter(this.answerText) };
    try {
      await postAnswer(data);
      this.pushDataLayer();
      this.resetModal();
      showToast({
        type: 'success',
        message: [IDS_UGC_QNA_TOAST_THANKS, IDS_UGC_QNA_TOAST_CHECK],
      });
    } catch (e) {
      handleErrorMessage(e);
      this.pushDataLayer(true, e);
      this.resetModal();
    }
  }

  pushDataLayer(isError = false, error = '') {
    const {
      productInfo: { productId, partNumber = '', displayName = '' },
      answerModalData: { questionId },
    } = state;
    const questionInfo = filterQuestionInfo(questionId);
    const { postedDateLocalized = '' } = questionInfo;

    const eventData = {
      event: 'form_submit',
      form_submit: {
        name: 'qna - product answer',
        complete: !isError,
        type: 'dynamic',
        error: '',
        data: {
          topic: '',
          question_date: postedDateLocalized,
          item_id: `${productId}`,
          item_part_number: partNumber,
          item_name: displayName,
        },
      },
    };

    if (isError) {
      eventData.form_submit = { ...eventData.form_submit, error };
    }

    addDataLayer(eventData);
  }

  handleCancel() {
    this.resetModal();
  }

  onInputChange({ detail: answerText }) {
    this.answerText = answerText;
    this.setSubmitButtonState();
    // textarea error
    if (!this.validateContent(this.answerText)) return;

    this.resetTextareaErrorMessage();
  }

  setSubmitButtonState() {
    this.isSubmitDisabled =
      this.answerText.length < MIN_CHARACTERS ||
      this.answerText.length > MAX_ANSWER_CHARACTERS ||
      this.answerText.trim() === '' ||
      hasHtmlTag(this.answerText);
  }

  validateContent(content) {
    const {
      qnaStaticLanguage: {
        IDS_UGC_QNA_TEXTAREA_LEAST_CHARACTERS,
        IDS_UGC_QNA_TEXTAREA_MORE_CHARACTERS = '',
        IDS_UGC_ENTER_PLAIN_TEXT_MESSAGE = '',
      },
    } = state;

    if (content.length < MIN_CHARACTERS) {
      this.isTextareaError = true;
      this.textareaMessage = IDS_UGC_QNA_TEXTAREA_LEAST_CHARACTERS;
      return false;
    }
    if (content.length > MAX_ANSWER_CHARACTERS) {
      this.isTextareaError = true;
      this.textareaMessage = stringFormat(IDS_UGC_QNA_TEXTAREA_MORE_CHARACTERS, MAX_ANSWER_CHARACTERS);
      return false;
    }
    if (hasHtmlTag(content)) {
      this.isTextareaError = true;
      this.textareaMessage = IDS_UGC_ENTER_PLAIN_TEXT_MESSAGE;
      return false;
    }
    return true;
  }

  resetTextareaErrorMessage() {
    this.isTextareaError = false;
    this.textareaMessage = '';
  }

  resetModal() {
    this.answerText = '';
    this.isSubmitDisabled = true;
    this.isTextareaError = false;
    this.textareaMessage = '';
    state.isShowAnswerModal = false;
    state.answerModalData = {
      questionId: '',
    };
  }

  renderTextarea() {
    const {
      qnaStaticLanguage: {
        IDS_UGC_QNA_ANSWER_PLACEHOLDER = '',
        IDS_UGC_QNA_MODAL_NOTICE = '',
        IDS_UGC_QNA_SUBMIT_AN_ANSWER_TIPS = '',
        IDS_UGC_QNA_SUBMIT_AN_ANSWER_TIPS_HONEST = '',
        IDS_UGC_QNA_SUBMIT_AN_ANSWER_TIPS_REWARD = '',
        IDS_MSG_SHOW_MORE = '',
        IDS_MSG_SHOW_LESS = '',
      },
      answerModalData: { questionId },
      isDesktop,
    } = state;
    const questionInfo = filterQuestionInfo(questionId);
    const { content } = questionInfo;
    return [
      <div class="mb-16">
        <ugc-ellipsis-text
          contentStyle={{ fontWeight: 'bold', lineHeight: '24px' }}
          buttonStyle={{ fontSize: isDesktop ? '16px' : '14px' }}
          iconSize={isDesktop ? '24' : '20'}
          line={isDesktop ? 3 : 6}
          text={content}
          readMore={IDS_MSG_SHOW_MORE}
          readLess={IDS_MSG_SHOW_LESS}
        />
      </div>,
      <ugc-textarea
        textareaName={IDS_UGC_QNA_ANSWER_PLACEHOLDER}
        style={{ display: 'block' }}
        height={161}
        textareaValue={this.answerText}
        isError={this.isTextareaError}
        errorMessage={this.textareaMessage}
        onInputChange={this.onInputChange.bind(this)}
      />,
      <p class="c-666 fs-12 text-start my-16">{IDS_UGC_QNA_MODAL_NOTICE}</p>,
      <div>
        <p class="fs-14 c-666 bold mb-8">{IDS_UGC_QNA_SUBMIT_AN_ANSWER_TIPS}</p>
        <div class="fs-14 c-666 mb-8">{stringFormat(IDS_UGC_QNA_SUBMIT_AN_ANSWER_TIPS_HONEST, '$10')}</div>
        <div class="fs-14 c-666">{stringFormat(IDS_UGC_QNA_SUBMIT_AN_ANSWER_TIPS_REWARD, '$0.10')}</div>
      </div>,
    ];
  }

  render() {
    const {
      qnaStaticLanguage: { IDS_UGC_QNA_ANSWER_MODAL_TITLE, IDS_UGC_QNA_MODAL_SUBMIT },
      isShowAnswerModal,
      isLogin,
    } = state;
    const disabled = isLogin && this.isSubmitDisabled;

    return (
      <ugc-drawer
        isShow={isShowAnswerModal}
        modalTitle={IDS_UGC_QNA_ANSWER_MODAL_TITLE}
        submitButtonText={IDS_UGC_QNA_MODAL_SUBMIT}
        type="primary"
        disabled={disabled}
        zIndex={modalZIndex['ugc-qna-answer-modal']}
        handleSubmit={this.handleSubmit.bind(this)}
        handleCancel={this.handleCancel.bind(this)}
        isGray
      >
        {isShowAnswerModal && (
          <div class="p-24">
            <div class="answer-form-wrap">{this.renderTextarea()}</div>
          </div>
        )}
      </ugc-drawer>
    );
  }
}
